import moment from "moment";
import { TFunction } from "i18next";

import { TimePeriodEnum } from "../interfaces/TimePeriod";

const translateKey = "app:utils:date";

/**
 * Given a time period, get the start time in UNIX seconds
 * eg. timePeriod = DAILY, start time will be today (start of day)
 * eg. timePeriod = WEEKLY, start time will be this monday (start of week)
 * @param timePeriod
 * @param customStartTime Optional custom start time in seconds. If not provided, use Date.now()
 */
export const getStartTimeSeconds = (
  timePeriod: TimePeriodEnum,
  customStartTime?: number
): number => {
  const startTime = customStartTime ? moment.unix(customStartTime) : moment();
  switch (timePeriod) {
    case TimePeriodEnum.HOURLY:
      return startTime.utc().startOf("hour").unix();
    case TimePeriodEnum.DAILY:
      return startTime.utc().startOf("day").unix();
    case TimePeriodEnum.WEEKLY:
      return startTime.utc().subtract(1, "week").startOf("day").unix();
    case TimePeriodEnum.MONTHLY:
      return startTime.utc().subtract(1, "months").startOf("day").unix();
    case TimePeriodEnum.THREE_MONTHS:
      return startTime.utc().subtract(3, "months").startOf("day").unix();
    case TimePeriodEnum.SIX_MONTHS:
      return startTime.utc().subtract(6, "months").startOf("day").unix();
    case TimePeriodEnum.YEARLY:
      return startTime.utc().subtract(1, "year").startOf("day").unix();
    default:
      return 0;
  }
};

/**
 * @param nanos Nanoseconds in string
 * @returns Number of seconds
 */
export const nanosToSeconds = (nanos: string | number) =>
  Number(String(nanos).slice(0, -9));

export const nanosToMillis = (nanos: string | number) =>
  Number(String(nanos).slice(0, -6));

/**
 * Epoch SECONDS from a timestamp whose unit the caller does not know, or
 * `undefined` when there is no usable time in it.
 *
 * The API sends `created_timestamp` in nanoseconds. Read as seconds it lands
 * about 56 billion years in the future, and `getTimeAgo` answers "just now" for
 * any future date -- so every order in the table read "Just Now", including ones
 * four months old. The bug is invisible precisely because the wrong answer looks
 * like a plausible one.
 *
 * The unit is inferred by magnitude rather than assumed, because these columns
 * are fed by several endpoints and a fixed assumption would break the moment one
 * of them differed. The thresholds are not close calls: epoch seconds do not
 * reach 1e12 until the year 33658, so anything at or above it is milliseconds or
 * finer, and anything at or above 1e15 is microseconds or finer.
 */
export const epochSecondsFrom = (
  value: string | number | null | undefined
): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  if (n >= 1e18) return n / 1e9; // nanoseconds
  if (n >= 1e15) return n / 1e6; // microseconds
  if (n >= 1e12) return n / 1e3; // milliseconds
  return n; // seconds
};

/**
 * @param seconds to nanos in string
 * @returns Number of nanoseconds
 */
export const secondsToNanos = (seconds: string | number) =>
  Number(seconds) * 1e9;

/**
 * Given a time period, return the appropriate resolution (interval) in seconds
 * @param timePeriod Time period
 */
export const getResolutionSecondsForTimePeriod = (
  timePeriod: TimePeriodEnum
): number => {
  switch (timePeriod) {
    case TimePeriodEnum.HOURLY:
      // 30 seconds
      return 30;
    case TimePeriodEnum.DAILY:
      // Every minute
      return 60;
    case TimePeriodEnum.WEEKLY:
      // Every 30 mins
      return 60 * 30;
    case TimePeriodEnum.MONTHLY:
      // Every hour
      return 60 * 60;
    case TimePeriodEnum.THREE_MONTHS:
      // Every 2 hours
      return 60 * 60 * 2;
    case TimePeriodEnum.SIX_MONTHS:
      // Every 4 hours
      return 60 * 60 * 4;
    case TimePeriodEnum.YEARLY:
      // Every Day
      return 60 * 60 * 24;
    default:
      return 0;
  }
};

/**
 * Given an expiry, returns the remaining time to expiry in weeks, days, hours, minutes, and seconds
 * @param expiry UNIX timestamp
 */
export const timeLeftToExpiry = (
  expiry: number | string,
  currentDate?: Date
) => {
  const exp = moment.unix(Number(expiry));
  const duration = moment.duration(exp.diff(currentDate ?? new Date()));

  // Get weeks and subtract from duration
  let weeks = duration.weeks();
  // Max is weeks, so if duration is >1 month
  // we use asWeeks() to convert it all into weeks
  if (duration.asWeeks() > 4) {
    weeks = Math.floor(duration.asWeeks());
  }
  duration.subtract(weeks, "weeks");

  // Get Days and subtract from duration
  const days = duration.days();
  duration.subtract(days, "days");

  // Get hours and subtract from duration
  const hours = duration.hours();
  duration.subtract(hours, "hours");

  // Get Minutes and subtract from duration
  const minutes = duration.minutes();
  duration.subtract(minutes, "minutes");

  const seconds = duration.seconds();

  // Never negative
  return {
    weeks: Math.max(0, weeks),
    days: Math.max(0, days),
    hours: Math.max(0, hours),
    minutes: Math.max(0, minutes),
    seconds: Math.max(0, seconds),
  };
};

/**
 * eg. "1D", "20H", etc.
 * @param expiry UNIX timestamp
 */
export const shorthandTimeLeftToExpiry = (
  t: TFunction,
  expiry: number | string,
  currentDate?: Date
) => {
  const { weeks, days, hours, minutes, seconds } = timeLeftToExpiry(
    expiry,
    currentDate
  );

  if (weeks) {
    return t(`${translateKey}:short_week`, { n: weeks });
  }
  if (days) {
    return t(`${translateKey}:short_day`, { n: days });
  }
  if (hours) {
    return t(`${translateKey}:short_hour`, { n: hours });
  }
  if (minutes) {
    return t(`${translateKey}:short_min`, { n: minutes });
  }

  // If a few seconds still remaining, return 1M
  if (seconds) {
    return t(`${translateKey}:short_one_min`);
  }
  return t(`${translateKey}:expired`);
};
/**
 * eg. "1W 2D 5H 26M"
 * @param expiry UNIX timestamp
 */
export const longhandTimeLeftToExpiry = (
  t: TFunction,
  expiry: number | string,
  currentDate?: Date,
  showSeconds?: boolean
) => {
  const { weeks, days, hours, minutes, seconds } = timeLeftToExpiry(
    expiry,
    currentDate
  );

  const timeLeft: string[] = [];
  if (weeks) {
    timeLeft.push(t(`${translateKey}:short_week`, { n: weeks }));
  }
  if (days) {
    timeLeft.push(t(`${translateKey}:short_day`, { n: days }));
  }
  if (hours) {
    timeLeft.push(t(`${translateKey}:short_hour`, { n: hours }));
  }
  if (minutes) {
    timeLeft.push(t(`${translateKey}:short_min`, { n: minutes }));
  } else if (seconds && !showSeconds) {
    // If a few seconds still remaining, return 1M
    timeLeft.push(t(`${translateKey}:short_one_min`));
  }

  if (seconds && showSeconds) {
    timeLeft.push(t(`${translateKey}:short_sec`, { n: seconds }));
  }

  if (!timeLeft.length) {
    return undefined;
  }

  // Only show the first 4 values
  return timeLeft.slice(0, 4).join(" ");
};

export const getTimeAgo = (pastDate: moment.MomentInput, t: TFunction) => {
  const compareToDate = moment(pastDate);
  const now = moment();
  const diff = now.diff(compareToDate);

  // Date must be in the past, else is "Just now"
  if (diff >= 0) {
    const duration = moment.duration(diff);
    const oneMonthAgo = now.clone().subtract(1, "month");
    if (compareToDate <= oneMonthAgo) {
      const value = Math.round(duration.asMonths());
      return value > 1
        ? t(`${translateKey}:n_months`, { n: value })
        : t(`${translateKey}:one_month`);
    }

    const oneWeekAgo = now.clone().subtract(1, "week");
    if (compareToDate <= oneWeekAgo) {
      const value = Math.round(duration.asWeeks());
      return value > 1
        ? t(`${translateKey}:n_weeks`, { n: value })
        : t(`${translateKey}:one_week`);
    }

    const oneDayAgo = now.clone().subtract(1, "day");
    if (compareToDate <= oneDayAgo) {
      const value = Math.round(duration.asDays());
      return value > 1
        ? t(`${translateKey}:n_days`, { n: value })
        : t(`${translateKey}:one_day`);
    }

    const oneHourAgo = now.clone().subtract(1, "hour");
    if (compareToDate <= oneHourAgo) {
      const value = Math.round(duration.asHours());
      return value > 1
        ? t(`${translateKey}:n_hours`, { n: value })
        : t(`${translateKey}:one_hour`);
    }

    const oneMinuteAgo = now.clone().subtract(1, "minute");
    if (compareToDate <= oneMinuteAgo) {
      const value = Math.round(duration.asMinutes());
      return value > 1
        ? t(`${translateKey}:n_mins`, { n: value })
        : t(`${translateKey}:one_min`);
    }
  }

  return t(`${translateKey}:just_now`);
};


export const formatTimestamp = (timestamp: number) => {
  const now = Date.now();
  const diff = now - timestamp;
  if (diff < 60000) {
    // Less than 1 minute
    return "now";
  } else if (diff < 3600000) {
    // Less than 1 hour
    return `${Math.floor(diff / 60000)}m ago`;
  } else if (diff < 86400000) {
    // Less than 1 day
    return `${Math.floor(diff / 3600000)}h ago`;
  } else if (diff < 7 * 86400000) {
    // Less than 1 week
    return `${Math.floor(diff / 86400000)}d ago`;
  } else {
    return `${Math.floor(diff / 86400000)}d ago`;
  }
};
